import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import SearchableDropdown from '../components/SearchableDropdown';
import { useToast } from '../contexts/ToastContext';
import { studentService } from '../services/studentService';
// import { Service } from '../services/Service';
import APIRequest from '../api/APIRequest';
import { useSelector } from 'react-redux';
import { classService } from '../services/classService';
import { fieldConfigService } from '../services/fieldConfigService';
import DynamicFields from '../components/forms/DynamicFields';
import CustomDatePicker from '../components/ui/CustomDatePicker';
import PhoneInput from '../components/ui/PhoneInput';

const formatPhoneForInput = (phone) => {
  if (!phone) return '+91 ';
  if (phone.startsWith('+')) {
    if (phone.includes(' ')) return phone;
    const match = phone.match(/^(\+\d{1,3})(\d+)$/);
    if (match) return `${match[1]} ${match[2]}`;
    return phone;
  }
  return `+91 ${phone}`;
};

const StudentEdit = ({ studentData: propStudentData }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [studentData, setStudentData] = useState(propStudentData);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(!propStudentData);
  const [saving, setSaving] = useState(false);
  const { user } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '+91',
    class: '',
    academicYear: '',
    regNo: '',
    dateOfBirth: '',
    contactPerson: [{ relation: '', phone: '', name: '' }],
    gender: 'male',
    motherLanguage: '',
    nationality: 'INDIA',
    aadharNumber: '',
    birthCertificateNumber: '',
    bloodGroup: '',
    address: '',
    password: '',
    customFields: {}
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [configFields, setConfigFields] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);

  // Fetch student dynamic fields
  useEffect(() => {
    const fetchStudentFields = async () => {
      try {
        const fields = await fieldConfigService.getEntityFields('student', user.token);
        setConfigFields(Array.isArray(fields) ? fields : []);
      } catch (err) {
        console.error('Error fetching student fields:', err);
      }
    };
    if (user.token) {
      fetchStudentFields();
    }
  }, [user.token]);

  // Handle custom field changes
  const handleCustomFieldChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      customFields: {
        ...(prev.customFields || {}),
        [name]: value
      }
    }));
  };



  // Get unique academic years from classes
  const getUniqueAcademicYears = () => {
    const academicYears = new Set();
    classes.forEach(cls => {
      if (cls.academicYearFrom && cls.academicYearTo) {
        const fromYear = cls.academicYearFrom.toString().length <= 4 
          ? cls.academicYearFrom 
          : new Date(cls.academicYearFrom).getFullYear();
        const toYear = cls.academicYearTo.toString().length <= 4 
          ? cls.academicYearTo 
          : new Date(cls.academicYearTo).getFullYear();
        const classAcademicYear = `${fromYear}-${toYear}`;
        academicYears.add(classAcademicYear);
      }
    });
    return Array.from(academicYears).sort();
  };

  const deriveYearFromClass = (studentClassId) => {
    if (!studentClassId || !classes.length) return '';
    const cls = classes.find(c => c._id === (studentClassId?._id || studentClassId));
    if (cls && cls.academicYearFrom && cls.academicYearTo) {
      const fromYear = cls.academicYearFrom.toString().length <= 4 
        ? cls.academicYearFrom 
        : new Date(cls.academicYearFrom).getFullYear();
      const toYear = cls.academicYearTo.toString().length <= 4 
        ? cls.academicYearTo 
        : new Date(cls.academicYearTo).getFullYear();
      return `${fromYear}-${toYear}`;
    }
    return '';
  };

  // Get classes filtered by academic year
  const getFilteredClasses = () => {
    if (!formData.academicYear) return classes;
    
    return classes.filter(cls => {
      if (!cls.academicYearFrom || !cls.academicYearTo) return false;
      const fromYear = cls.academicYearFrom.toString().length <= 4 
        ? cls.academicYearFrom 
        : new Date(cls.academicYearFrom).getFullYear();
      const toYear = cls.academicYearTo.toString().length <= 4 
        ? cls.academicYearTo 
        : new Date(cls.academicYearTo).getFullYear();
      const classAcademicYear = `${fromYear}-${toYear}`;
      return classAcademicYear === formData.academicYear;
    });
  };

  useEffect(() => {
    // If student data is passed as props, use it directly
    if (propStudentData) {
      setStudentData(propStudentData);
        setFormData({
          name: propStudentData.name || '',
          email: propStudentData.email || '',
          phone: formatPhoneForInput(propStudentData.phone),
          class: propStudentData.class || '',
          academicYear: propStudentData.academicYear || '',
          regNo: propStudentData.regNo || '',
          dateOfBirth: propStudentData.dateOfBirth || '',
          contactPerson: (propStudentData.contactPerson || [{ relation: 'Father', phone: '', name: '' }]).map(cp => ({
            ...cp,
            phone: formatPhoneForInput(cp.phone)
          })),
          gender: propStudentData.gender || 'male',
          motherLanguage: propStudentData.motherLanguage || '',
          nationality: propStudentData.nationality || '',
          aadharNumber: propStudentData.aadharNumber || '',
          birthCertificateNumber: propStudentData.birthCertificateNumber || '',
          bloodGroup: propStudentData.bloodGroup || '',
          address: propStudentData.address || '',
          password: '',
          customFields: propStudentData.customFields || {}
        });
      setLoading(false);
      return;
    }

    // Otherwise, fetch student data
    const fetchStudentData = async () => {
      setLoading(true);
      try {
        const api = new APIRequest();
        const response = await api.request(`/admin/getStudent?id=${id}`, 'GET', {}, user.token);
        
        if (response.success) {
          setStudentData(response.student);
          setFormData({
            name: response.student.name || '',
            email: response.student.email || '',
            phone: formatPhoneForInput(response.student.phone),
            class: response.student.class || '',
            academicYear: response.student.academicYear || '',
            regNo: response.student.regNo || '',
            dateOfBirth: response.student.dateOfBirth || '',
            contactPerson: (response.student.contactPerson || [{ relation: 'Father', phone: '', name: '' }]).map(cp => ({
              ...cp,
              phone: formatPhoneForInput(cp.phone)
            })),
            gender: response.student.gender || 'male',
            motherLanguage: response.student.motherLanguage || '',
            nationality: response.student.nationality || '',
            aadharNumber: response.student.aadharNumber || '',
            birthCertificateNumber: response.student.birthCertificateNumber || '',
            bloodGroup: response.student.bloodGroup || '',
            address: response.student.address || '',
            password: '',
            customFields: response.student.customFields || {}
          });
        } else {
          error(response.message || 'Failed to fetch student details');
        }
      } catch (err) {
        console.error('Error fetching student data:', err);
        error('Failed to fetch student details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchStudentData();
    }
  }, [id, user.token, error, propStudentData]);

  // Fetch classes data
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const classesData = await classService.getAllClasses(user.token);
        setClasses(classesData);
      } catch (err) {
        console.error('Error fetching classes:', err);
      }
    };
    fetchClasses();
  }, [user.token]);

  // Auto-derive academic year if missing
  useEffect(() => {
    if (formData.class && !formData.academicYear && classes.length > 0) {
      const derivedYear = deriveYearFromClass(formData.class);
      if (derivedYear) {
        setFormData(prev => ({ ...prev, academicYear: derivedYear }));
      }
    }
  }, [formData.class, classes, formData.academicYear, deriveYearFromClass]);



  const addContactPerson = () => {
    setFormData({
      ...formData,
      contactPerson: [...formData.contactPerson, { relation: '', phone: '', name: '' }]
    });
  };

  const removeContactPerson = (index) => {
    const updated = formData.contactPerson.filter((_, i) => i !== index);
    setFormData({ ...formData, contactPerson: updated });
  };

  const updateContactPerson = (index, field, value) => {
    let finalValue = value;
    if (field === 'phone') {
      // Ensure +91 prefix for phone numbers
      if (value && !value.startsWith('+91') && !value.startsWith('+')) {
        finalValue = '+91' + value;
      } else if (value && value.startsWith('+') && !value.startsWith('+91')) {
        finalValue = '+91' + value.substring(1);
      } else if (!value) {
        finalValue = '+91';
      }
    }
    const updated = formData.contactPerson.map((contact, i) => 
      i === index ? { ...contact, [field]: finalValue } : contact
    );
    setFormData({ ...formData, contactPerson: updated });
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s()]/g, ''));
  };

  const validateAadhar = (aadhar) => {
    // Remove all spaces and dashes
    const cleanAadhar = aadhar.replace(/[\s-]/g, '');
    // Check if it's exactly 12 digits and starts with 2-9
    const aadharRegex = /^[2-9]{1}[0-9]{11}$/;
    return aadharRegex.test(cleanAadhar);
  };

  const validateBirthCertificateNumber = (certNumber) => {
    // Birth certificate number should be alphanumeric and at least 5 characters
    const cleanCertNumber = certNumber.trim();
    return cleanCertNumber.length >= 5;
  };

  const validateForm = () => {
    const errors = {};

    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length < 3) {
      errors.name = 'Name must be at least 3 characters';
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Phone validation
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      errors.phone = 'Please enter a valid phone number';
    }

    // Academic Year validation
    if (!formData.academicYear) {
      errors.academicYear = 'Academic year selection is required';
    }

    // Class validation
    if (!formData.class || !formData.class._id) {
      errors.class = 'Class and Section selection is required';
    }

    // Date of birth validation
    if (!formData.dateOfBirth) {
      errors.dateOfBirth = 'Date of birth is required';
    } else {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      if (birthDate >= today) {
        errors.dateOfBirth = 'Date of birth must be in the past';
      }
    }

    // Mother language validation
    if (!formData.motherLanguage.trim()) {
      errors.motherLanguage = 'Mother language is required';
    }

    // Nationality validation
    if (!formData.nationality.trim()) {
      errors.nationality = 'Nationality is required';
    }

    // Blood group validation
    if (!formData.bloodGroup) {
      errors.bloodGroup = 'Blood group is required';
    }

    // Aadhar validation (optional)
    if (formData.aadharNumber.trim() && !validateAadhar(formData.aadharNumber)) {
      errors.aadharNumber = 'Please enter a valid 12-digit Aadhar number';
    }

    // Birth Certificate Number validation
    // Birth Certificate Number validation (optional)
    if (formData.birthCertificateNumber.trim() && !validateBirthCertificateNumber(formData.birthCertificateNumber)) {
      errors.birthCertificateNumber = 'Birth certificate number must be at least 5 characters';
    }

    // Address validation
    if (!formData.address.trim()) {
      errors.address = 'Address is required';
    }


    // Contact person validation
    if (!formData.contactPerson || formData.contactPerson.length === 0) {
      errors.contactPerson = 'At least one contact person is required';
    } else {
      formData.contactPerson.forEach((contact, index) => {
        if (!contact.name.trim()) {
          errors[`contactPerson_${index}_name`] = 'Contact person name is required';
        }
        if (!contact.relation.trim()) {
          errors[`contactPerson_${index}_relation`] = 'Relation is required';
        }
        if (!contact.phone.trim()) {
          errors[`contactPerson_${index}_phone`] = 'Contact person phone is required';
        } else if (!validatePhone(contact.phone)) {
          errors[`contactPerson_${index}_phone`] = 'Please enter a valid phone number';
        }
      });
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    console.log(formData, 'formData');
    setSaving(true);
    
    const payload = new FormData();
    payload.append('name', formData.name);
    payload.append('email', formData.email);
    payload.append('phone', formData.phone);
    payload.append('class', JSON.stringify(formData.class));
    payload.append('academicYear', formData.academicYear);
    payload.append('dateOfBirth', formData.dateOfBirth);
    payload.append('contactPerson', JSON.stringify(formData.contactPerson));
    payload.append('gender', formData.gender);
    payload.append('motherLanguage', formData.motherLanguage);
    payload.append('nationality', formData.nationality);
    payload.append('aadharNumber', formData.aadharNumber);
    payload.append('birthCertificateNumber', formData.birthCertificateNumber);
    payload.append('bloodGroup', formData.bloodGroup);
    payload.append('address', formData.address);
    if (formData.password) {
        payload.append('password', formData.password);
    }
    payload.append('customFields', JSON.stringify(formData.customFields || {}));

    if (formData.image instanceof File) {
        payload.append('image', formData.image);
    }

    try {
      const response = await studentService.updateStudent(id, payload, user.token);
      if (response.success) {
        success('Student updated successfully!');
        navigate(`/students/${id}`);
      } else {
        error(response.message || 'Failed to update student. Please try again.');
      }
    } catch (err) {
      console.error('Error updating student:', err);
      error('Failed to update student. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleBackToList = () => {
    navigate('/students');
  };

  const [activeTab, setActiveTab] = useState('personal');

  const tabs = [
    { id: 'personal', label: 'Personal Information' },
    { id: 'academic', label: 'Academic Details' },
    { id: 'additional', label: 'Additional Information' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 -m-6 pb-12">
      {/* Cover Header */}
      <div className="h-32 bg-gradient-to-r from-blue-600 to-indigo-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.1] bg-[size:20px_20px]" />
        
        <div className="absolute inset-0 flex items-center justify-center">
          <h1 className="text-2xl font-black text-white/90 tracking-tight uppercase">Edit Student Profile</h1>
        </div>

        <button
          onClick={handleBackToList}
          className="absolute top-6 left-6 p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all backdrop-blur-md flex items-center gap-2 group z-10"
        >
          <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium">Back</span>
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-6 -mt-16">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* Left Column: Profile Preview Card */}
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-white rounded-3xl shadow-xl shadow-blue-900/5 border border-slate-100 overflow-hidden">
                <div className="p-8 flex flex-col items-center text-center">
                  <div className="relative mb-6 group">
                    <div className="w-32 h-32 rounded-3xl overflow-hidden ring-4 ring-white shadow-lg shadow-blue-900/10 bg-slate-50 relative">
                      {imagePreview ? (
                        <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                      ) : studentData?.image ? (
                        <img src={`/${studentData.image}`} alt="Current" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-blue-50 text-blue-600">
                          <span className="text-3xl font-black">
                            {formData.name ? formData.name.split(' ').map(n => n[0]).join('').slice(0, 2) : 'S'}
                          </span>
                        </div>
                      )}
                      <label htmlFor="student-image-edit" className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                        <span className="text-white text-xs font-bold uppercase tracking-widest">Update</span>
                        <input
                          type="file"
                          accept=".png,.jpg,.jpeg"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setFormData({ ...formData, image: file });
                              const reader = new FileReader();
                              reader.onloadend = () => setImagePreview(reader.result);
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="hidden"
                          id="student-image-edit"
                        />
                      </label>
                    </div>
                  </div>

                  <h2 className="text-2xl font-bold text-slate-900 mb-1">{formData.name || 'Student Name'}</h2>
                  <p className="text-slate-400 font-bold text-[10px] tracking-[0.2em] uppercase mb-6">
                    Registration: {formData.regNo || 'PENDING'}
                  </p>

                  <div className="w-full pt-6 border-t border-slate-100">
                    <button
                      type="submit"
                      disabled={saving}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-2xl transition-all shadow-lg shadow-blue-600/30 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Saving Changes...</span>
                        </>
                      ) : (
                        <span>Save Student Profile</span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/students/${id}`)}
                      className="w-full mt-3 bg-white hover:bg-slate-50 text-slate-600 font-bold py-4 rounded-2xl border border-slate-200 transition-all flex items-center justify-center gap-2"
                    >
                      Cancel & Exit
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Tabbed Form */}
            <div className="lg:col-span-8 space-y-8">
              {/* Tabs Header */}
              <div className="bg-white p-2 rounded-2xl shadow-xl shadow-blue-900/5 border border-slate-100 flex gap-2 overflow-x-auto no-scrollbar">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 min-w-[140px] py-3 px-4 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
                      activeTab === tab.id 
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' 
                      : 'text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="bg-white rounded-3xl shadow-xl shadow-blue-900/5 border border-slate-100 p-8 min-h-[600px] animate-fadeIn">
                
                {activeTab === 'personal' && (
                  <div className="space-y-8">
                    {/* Mobile Login Credentials (Integrated) */}
                    <div className="p-6 bg-blue-50/50 rounded-[2rem] border border-blue-100 mb-2">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Mobile Portal Access</h4>
                        <span className="px-3 py-1 bg-blue-100 text-blue-600 rounded-lg text-[10px] font-black uppercase">Required for App</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Portal Login ID</label>
                          <input
                            type="text"
                            value={formData.regNo || 'Auto-generated'}
                            disabled
                            className="w-full px-4 py-3 bg-slate-200/50 border border-slate-200 rounded-xl text-slate-500 font-bold cursor-not-allowed"
                          />
                        </div>
                        <FormField label="Access Password *" error={validationErrors.password}>
                          <input
                            type="text"
                            value={formData.password}
                            onChange={(e) => setFormData({...formData, password: e.target.value})}
                            placeholder="Set login password"
                            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all font-mono"
                          />
                        </FormField>
                      </div>
                    </div>

                    <div className="border-b border-slate-50 pb-4">
                      <h3 className="text-xl font-bold text-slate-900">Personal Information</h3>
                      <p className="text-sm text-slate-500">Student identity and contact details</p>
                    </div>

                    {/* Core Enrollment Details (Restored Visibility) */}
                    <div className="bg-slate-50/50 p-6 rounded-3xl border border-slate-200 grid grid-cols-1 md:grid-cols-4 gap-6">
                      <FormField label="Academic Year *" error={validationErrors.academicYear}>
                        <select
                          value={formData.academicYear}
                          onChange={(e) => setFormData({...formData, academicYear: e.target.value, class: ''})}
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all appearance-none cursor-pointer font-bold text-blue-600"
                        >
                          <option value="">Select Year</option>
                          {getUniqueAcademicYears().map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </FormField>

                      <FormField label="Grade / Class *" error={validationErrors.class}>
                        <SearchableDropdown
                          options={[...new Set(getFilteredClasses().map(cls => cls.name))].map(name => ({ _id: name, name }))}
                          value={formData.class?.name ? { _id: formData.class.name, name: formData.class.name } : ''}
                          onChange={(selected) => setFormData(prev => ({ ...prev, class: { ...prev.class, name: selected.name } }))}
                          placeholder={formData.academicYear ? "Select Grade" : "Select Year First"}
                          disabled={!formData.academicYear}
                        />
                      </FormField>
 
                      <FormField label="Section *">
                        <SearchableDropdown
                          options={getFilteredClasses()
                            .filter(cls => cls.name === formData.class?.name)
                            .map(cls => ({ ...cls, section: cls.section || 'General' }))}
                          value={formData.class?._id ? formData.class : ''}
                          onChange={(selectedSection) => setFormData(prev => ({ ...prev, class: selectedSection }))}
                          labelKey="section"
                          placeholder={formData.class?.name ? "Select Section" : "Select Grade First"}
                          disabled={!formData.class?.name}
                        />
                      </FormField>

                      <FormField label="Registration ID">
                        <input
                          type="text"
                          value={formData.regNo || 'N/A'}
                          readOnly
                          className="w-full px-4 py-3 bg-slate-200/50 border border-slate-200 rounded-xl text-slate-500 font-bold cursor-not-allowed"
                        />
                      </FormField>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField label="Full Name *" error={validationErrors.name}>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="Enter full name"
                          className="w-full px-4 py-3 bg-slate-50/50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                        />
                      </FormField>

                      <FormField label="Email Address *" error={validationErrors.email}>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="example@student.com"
                          className="w-full px-4 py-3 bg-slate-50/50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                        />
                      </FormField>

                      <FormField label="Phone Number *" error={validationErrors.phone}>
                        <PhoneInput
                          value={formData.phone}
                          onChange={(val) => setFormData({ ...formData, phone: val })}
                        />
                      </FormField>

                      <FormField label="Date of Birth *" error={validationErrors.dateOfBirth}>
                        <CustomDatePicker
                          name="dateOfBirth"
                          value={formData.dateOfBirth}
                          onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})}
                          maxYear={new Date().getFullYear()}
                        />
                      </FormField>

                      <FormField label="Gender">
                        <select
                          value={formData.gender}
                          onChange={(e) => setFormData({...formData, gender: e.target.value})}
                          className="w-full px-4 py-3 bg-slate-50/50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all appearance-none cursor-pointer"
                        >
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </FormField>

                      <FormField label="Mother Tongue *" error={validationErrors.motherLanguage}>
                        <input
                          type="text"
                          value={formData.motherLanguage}
                          onChange={(e) => setFormData({...formData, motherLanguage: e.target.value})}
                          placeholder="e.g. English, Hindi"
                          className="w-full px-4 py-3 bg-slate-50/50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                        />
                      </FormField>

                      <div className="md:col-span-2">
                        <FormField label="Home Address *" error={validationErrors.address}>
                          <textarea
                            value={formData.address}
                            onChange={(e) => setFormData({...formData, address: e.target.value})}
                            placeholder="Complete residential address"
                            className="w-full px-4 py-3 bg-slate-50/50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all resize-none"
                            rows={3}
                          />
                        </FormField>
                      </div>
                    </div>

                    {/* Emergency Contacts */}
                    <div className="pt-8 mt-8 border-t border-slate-50">
                      <div className="flex justify-between items-center mb-6">
                        <div>
                          <h4 className="text-sm font-black text-slate-400 uppercase tracking-widest">Emergency Contacts</h4>
                          <p className="text-xs text-slate-500 mt-1">Provide at least one primary contact</p>
                        </div>
                        <button
                          type="button"
                          onClick={addContactPerson}
                          className="px-4 py-2 bg-blue-50 text-blue-600 font-bold text-xs rounded-xl hover:bg-blue-100 transition-colors"
                        >
                          + Add Contact
                        </button>
                      </div>
                      
                      <div className="space-y-4">
                        {formData.contactPerson?.map((contact, index) => (
                          <div key={index} className="p-6 bg-slate-50/50 rounded-3xl border border-slate-100 relative group">
                            {formData.contactPerson.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeContactPerson(index)}
                                className="absolute -top-2 -right-2 w-8 h-8 bg-red-50 text-red-500 rounded-full flex items-center justify-center hover:bg-red-500 hover:text-white transition-all shadow-sm opacity-0 group-hover:opacity-100"
                              >
                                <X size={14} />
                              </button>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                              <FormField label="Name" error={validationErrors[`contactPerson_${index}_name`]}>
                                <input
                                  type="text"
                                  value={contact.name}
                                  onChange={(e) => updateContactPerson(index, 'name', e.target.value)}
                                  className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                                />
                              </FormField>
                              <FormField label="Relation" error={validationErrors[`contactPerson_${index}_relation`]}>
                                <select
                                  value={contact.relation}
                                  onChange={(e) => updateContactPerson(index, 'relation', e.target.value)}
                                  className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all appearance-none cursor-pointer"
                                >
                                  <option value="Father">Father</option>
                                  <option value="Mother">Mother</option>
                                  <option value="Grandfather">Grandfather</option>
                                  <option value="Grandmother">Grandmother</option>
                                  <option value="Guardian">Guardian</option>
                                </select>
                              </FormField>
                              <FormField label="Phone" error={validationErrors[`contactPerson_${index}_phone`]}>
                                <PhoneInput
                                  value={contact.phone}
                                  onChange={(val) => updateContactPerson(index, 'phone', val)}
                                />
                              </FormField>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'academic' && (
                  <div className="space-y-8">
                    <div className="border-b border-slate-50 pb-4">
                      <h3 className="text-xl font-bold text-slate-900">Academic Details</h3>
                      <p className="text-sm text-slate-500">School records and institutional information</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                      <FormField label="Blood Group *" error={validationErrors.bloodGroup}>
                        <select
                          value={formData.bloodGroup}
                          onChange={(e) => setFormData({...formData, bloodGroup: e.target.value})}
                          className="w-full px-4 py-3 bg-slate-50/50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all appearance-none cursor-pointer"
                        >
                          <option value="">Select Group</option>
                          <option value="A+">A+</option>
                          <option value="A-">A-</option>
                          <option value="B+">B+</option>
                          <option value="B-">B-</option>
                          <option value="AB+">AB+</option>
                          <option value="AB-">AB-</option>
                          <option value="O+">O+</option>
                          <option value="O-">O-</option>
                        </select>
                      </FormField>

                      <FormField label="Aadhar Card Number" error={validationErrors.aadharNumber}>
                        <input
                          type="text"
                          value={formData.aadharNumber}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 12);
                            setFormData({ ...formData, aadharNumber: val });
                          }}
                          placeholder="12-digit UIDAI number"
                          className="w-full px-4 py-3 bg-slate-50/50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                        />
                      </FormField>

                      <FormField label="Birth Certificate No." error={validationErrors.birthCertificateNumber}>
                        <input
                          type="text"
                          value={formData.birthCertificateNumber}
                          onChange={(e) => setFormData({ ...formData, birthCertificateNumber: e.target.value.toUpperCase() })}
                          placeholder="e.g. MH-202X-XXXXX"
                          className="w-full px-4 py-3 bg-slate-50/50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                        />
                      </FormField>
                    </div>
                  </div>
                )}


                {activeTab === 'additional' && (
                  <div className="space-y-8">
                    <div className="border-b border-slate-50 pb-4">
                      <h3 className="text-xl font-bold text-slate-900">Additional Information</h3>
                      <p className="text-sm text-slate-500">Custom fields and specific metadata</p>
                    </div>

                    {configFields.length > 0 ? (
                      <div className="bg-slate-50/30 p-8 rounded-3xl border border-slate-100">
                        <DynamicFields
                          fields={configFields}
                          formData={formData.customFields || {}}
                          onChange={handleCustomFieldChange}
                          validationErrors={validationErrors}
                        />
                      </div>
                    ) : (
                      <div className="h-48 flex items-center justify-center bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200">
                        <p className="text-slate-400 font-medium">No additional fields configured for students</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Internal component for form fields with error handling
const FormField = ({ label, children, error }) => (
  <div className="space-y-1">
    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] block pl-1">{label}</label>
    <div className="relative">
      {children}
    </div>
    {error && (
      <p className="text-[10px] font-bold text-red-500 pl-1 mt-1 uppercase tracking-wide animate-pulse">
        {error}
      </p>
    )}
  </div>
);

export default StudentEdit;
